import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/utils/cn';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'black';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  fullWidth?: boolean;
  outline?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--user-green)] text-white hover:bg-[var(--color-accent-hover)] focus-visible:ring-[var(--user-green)]',
  secondary:
    'bg-[var(--color-surface-muted)] text-[var(--color-foreground)] hover:bg-[var(--color-border)] focus-visible:ring-[var(--user-green)]',
  ghost:
    'bg-transparent text-[var(--color-foreground)] hover:bg-[var(--color-surface-muted)] focus-visible:ring-[var(--user-green)]',
  danger:
    'bg-[var(--color-danger)] text-white hover:opacity-90 focus-visible:ring-[var(--color-danger)]',
  black:
    'bg-[var(--primary-black)] text-white hover:opacity-90 focus-visible:ring-[var(--primary-black)]',
};

// Outlined look per variant — uses `ring` instead of `border` so 1px line
// doesn't shift button height, and replaces the variant's solid bg + fg.
const outlineStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-transparent text-[var(--user-green)] ring-1 ring-inset ring-[var(--user-green)] hover:bg-[var(--color-accent)]/10 focus-visible:ring-[var(--user-green)]',
  secondary:
    'bg-transparent text-[var(--secondary-grey)] ring-1 ring-inset ring-[var(--secondary-grey)] hover:bg-[var(--secondary-grey)]/10 focus-visible:ring-[var(--secondary-grey)]',
  ghost:
    'bg-transparent text-[var(--color-foreground)] ring-1 ring-inset ring-[var(--color-foreground)] hover:bg-[var(--color-foreground)]/10 focus-visible:ring-[var(--color-foreground)]',
  danger:
    'bg-transparent text-[var(--color-danger)] ring-1 ring-inset ring-[var(--color-danger)] hover:bg-[var(--color-danger)]/10 focus-visible:ring-[var(--color-danger)]',
  black:
    'bg-transparent text-[var(--primary-black)] ring-1 ring-inset ring-[var(--primary-black)] hover:bg-[var(--primary-black)]/10 focus-visible:ring-[var(--primary-black)]',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-sm',
  lg: 'h-12 px-6 py-2.5 text-base',
};

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  fullWidth = false,
  outline = false,
  className,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-sm font-medium transition-colors cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-60',
        outline ? outlineStyles[variant] : variantStyles[variant],
        sizeStyles[size],
        fullWidth && 'w-full',
        className,
      )}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading ? <Spinner size="sm" label="Processing" /> : leftIcon}
      {children}
    </button>
  );
}
